import './styles/Listing.scss';

import { useApolloClient, useMutation } from '@apollo/client';
import { Grid } from '@mui/material';

import { Formik } from 'formik';
import { Gateway } from 'index';
import { omit, omitBy, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import CREATE_MUTATION from 'gql/mutations/addListing.gql';
import UPDATE_MUTATION from 'gql/mutations/updateListing.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';
import QUERY from 'gql/queries/getListing.gql';

import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import Category from './components/Category';
import Offer from './components/Offer';
import PersonalContacts from './components/PersonalContacts';
import Preview from './components/Preview';
import { listingInitialValues, listingValidationSchema } from './validation';

interface Props {
  onCancel: Function;
  closeHandler: Function;
  listing?: any;
  setDisabledCancel?: Function;
}

export default function Listing(props: Props) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const [createListing] = useMutation(CREATE_MUTATION);
  const [updateListing] = useMutation(UPDATE_MUTATION);
  const client = useApolloClient();
  const data = client.readQuery({ query: USER_QUERY });

  const steps = [
    t(translations.pages.listings.steps.category()),
    t(translations.pages.listings.steps.offer()),
    t(translations.pages.listings.steps.personalContacts()),
    t(translations.pages.listings.steps.review())
  ];

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    const omitFields = ['__typename', 'id', 'offerText', 'photos'];
    let saveBody: any = {
      ...omit(values, omitFields),
      offerText: values.offerText.replace(/<p><br><\/p>/g, ''),
      author: pick(data.currentUser, ['uid', 'email', 'firstName', 'lastName'])
    };

    if (saveBody.author.uid) {
      saveBody.author.id = saveBody.author.uid;
      delete saveBody.author.uid;
    }

    if (values.photos) {
      // Save logo first
      const formData = new FormData();
      values.photos.forEach(p => {
        formData.append('files', p);
      });
      const { data: fileURIs } = await Gateway.post('/upload/bulletin/files', formData);
      saveBody.photos = fileURIs;
    }

    if (values.id) {
      delete saveBody.author;
      try {
        await updateListing({
          variables: {
            id: values.id,
            data: omitBy(saveBody, v => v === null)
          },
          refetchQueries: ['GetListings', { query: QUERY, variables: { id: values.id } }]
        });
        props.closeHandler();
        dispatch(AlertActions.success(t(translations.pages.listings.alerts.saveSuccessful())));
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.listings.alerts.saveFailed())));
      }
    } else {
      try {
        await createListing({
          variables: {
            data: omitBy(saveBody, v => v === null)
          },
          refetchQueries: ['GetListings']
        });
        props.closeHandler();
        dispatch(AlertActions.success(t(translations.pages.listings.alerts.saveSuccessful())));
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.listings.alerts.saveFailed())));
      }
    }
    setSubmitting(false);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const ListingStep = ({ step }) => {
    switch (step) {
      case 0:
        return <Category />;
      case 1:
        return <Offer />;
      case 2:
        return <PersonalContacts />;
      case 3:
        return <Preview />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="listingModal">
      <Formik
        isInitialValid={!!props.listing}
        initialValues={listingInitialValues(props.listing, data.currentUser)}
        onSubmit={values => {
          handleSaveClick(values);
        }}
        validate={values => {
          return validateForm(values, yup.object().shape(listingValidationSchema(t)), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          stepValidator={() => true}
          steps={steps}
          onCancel={props.onCancel}
        >
          {step => (
            <Grid container className="listingModal__body">
              <ListingStep step={step} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
